import dayjs from "dayjs";
import { lazy } from "react";
import { useNavigate } from "react-router-dom";
import * as routes from "../data/links/routes";
import { errorTexts } from "../data/assets/texts";
import useScreenSize from "../context/ScreenContext";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useNotifications } from "../context/NotificationContext";
import { getAllUsers, getDeviceStatistics, blockUser } from "../api/admin";
import {
  FormButton,
  scrollBar,
  MainTitle,
  MainText,
} from "../data/style/Style";
import useChallenges from "../context/ChallengeContext";

const Box = lazy(() => import("@mui/material/Box"));
const Stack = lazy(() => import("@mui/material/Stack"));

const Admin = () => {
  const navigate = useNavigate();
  const { mobileDevice } = useScreenSize();
  const { enableNotification } = useNotifications();

  const { data: users, isPending: usersPending } = useQuery({
    queryKey: ["users", "all"],
    queryFn: ({ signal }) => getAllUsers({ signal }),
    staleTime: 10 * 60 * 1000,
  });

  const { data: deviceStats, isPending: deviceStatsPending } = useQuery({
    queryKey: ["device", "statistics"],
    queryFn: ({ signal }) => getDeviceStatistics({ signal }),
    staleTime: 10 * 60 * 1000,
  });

  const BlockUserMutation = useMutation({
    mutationFn: blockUser,
    onSuccess: (response) =>
      enableNotification(response.data.message, "success"),
    onError: (error) => {
      if (error.response.status === 400) {
        enableNotification(errorTexts.system_error, "error");
        return;
      }

      if (error.response.status >= 403) navigate(routes.LOGIN);

      if (error.response.status >= 500)
        enableNotification(errorTexts.system_error, "error");
    },
  });

  let handleBlockUser = (userId) => BlockUserMutation.mutate({ userId });

  return (
    <Stack height={"88%"} direction={"row"} gap={5}>
      {!usersPending && (
        <Stack sx={scrollBar} overflow={"auto"} width={"50%"} height={"50vh"}>
          <MainTitle mobile={mobileDevice}>Users ({users.length})</MainTitle>
          {users.map((user) => (
            <Stack direction={"row"} gap={1}>
              <MainText>{user.id}</MainText>
              <MainText> ||</MainText>
              <MainText>
                {user.first_name}&nbsp;{user.last_name}
              </MainText>
              <MainText> ||</MainText>
              <MainText>{user.gender === 0 ? "Male" : "Female"}</MainText>
              <MainText> ||</MainText>
              <MainText>
                {user.verified === 0 ? "Not Verified" : "Verified"}
              </MainText>
              <MainText> ||</MainText>
              <MainText>
                {user.level === 0
                  ? "Rookie"
                  : user.level === 1
                    ? "Scaled"
                    : user.level === 2
                      ? "Intermediate"
                      : "Rx"}
              </MainText>
              <MainText> ||</MainText>
              <MainText>
                {dayjs(user.date_joined).format("DD/MM/YYYY")}
              </MainText>
              <Box>
                <FormButton onClick={() => handleBlockUser(user.id)}>
                  <MainText>Block</MainText>
                </FormButton>
              </Box>
            </Stack>
          ))}
        </Stack>
      )}
      <Stack gap={2}>
        <MainTitle>Device Statistics</MainTitle>
        <Stack direction={"row"} gap={4}>
          {!deviceStatsPending && (
            <>
              <MainText>Android: {deviceStats.android}</MainText>
              <MainText>Ios: {deviceStats.ios}</MainText>
            </>
          )}
        </Stack>
      </Stack>
    </Stack>
  );
};

export default Admin;
